import { useState, type RefObject } from "react";
import { Pagination } from "../../ui/pagination.js";
import { CollapsibleSection, FieldGrid } from "../sectionShared.js";

export interface AuditEntry {
  id: string; field: string | null; action: string;
  oldValue: string | null; newValue: string | null;
  metadata: Record<string, unknown> | null;
  performedAt: string;
  performedBy: { email: string; employee: { firstName: string; lastName: string } | null };
}

interface Props {
  sectionRef: RefObject<HTMLDivElement>;
  auditLog: AuditEntry[];
}

const PAGE_SIZE = 10;

export function ChangeHistorySection({ sectionRef, auditLog }: Props) {
  const entries = auditLog.filter(e => e.action !== "SAVE");
  const entryCount = entries.length;
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(entryCount / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageEntries = entries.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  return (
    <CollapsibleSection
      id="history"
      sectionRef={sectionRef}
      title={<>Change History <span style={{ fontWeight: 500, color: "var(--color-surface-400)" }}>({entryCount} entries)</span></>}
    >
      <>
          {auditLog.length === 0 && (
            <p style={{ fontSize: 12, color: "var(--color-surface-400)", margin: "4px 0" }}>No changes recorded yet.</p>
          )}
          {pageEntries.map((entry) => (
            <FieldGrid
              key={entry.id}
              gap="sm"
              className="grid-cols-1 border-b border-surface-100 py-2.5 text-[13px] sm:grid-cols-2 lg:grid-cols-[minmax(0,160px)_minmax(0,140px)_minmax(0,1fr)_minmax(0,1fr)]"
            >
              <span style={{ color: "var(--color-surface-500)" }}>{new Date(entry.performedAt).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
              {/* break-words / break-all: an audit value is one unbreakable token
                  (an ISO timestamp, a cuid), so capping the grid track isn't enough
                  -- without a break opportunity the text paints past the card. */}
              <span className="break-words" style={{ fontWeight: 600 }}>
                {entry.performedBy.employee
                  ? `${entry.performedBy.employee.firstName} ${entry.performedBy.employee.lastName}`
                  : entry.performedBy.email}
              </span>
              <span className="break-words" style={{ color: "var(--color-surface-700)" }}>
                {entry.field ?? entry.action}
              </span>
              <span className="break-all" style={{ color: "var(--color-surface-600)", fontFamily: "monospace", fontSize: 12 }}>
                {entry.oldValue != null && <span style={{ color: "var(--color-error)" }}>{String(entry.oldValue).slice(0, 40)}</span>}
                {entry.oldValue != null && entry.newValue != null && <span style={{ margin: "0 4px" }}>→</span>}
                {entry.newValue != null && <span style={{ color: "var(--color-success)" }}>{String(entry.newValue).slice(0, 40)}</span>}
              </span>
            </FieldGrid>
          ))}
          <Pagination
            meta={{ total: entryCount, page: currentPage, limit: PAGE_SIZE, totalPages }}
            onPageChange={setPage}
          />
          {auditLog.length > 0 && auditLog.every(e => e.action === "SAVE") && (
            <p style={{ fontSize: 12, color: "var(--color-surface-400)", margin: "8px 0" }}>
              Profile was saved but no field values changed.
            </p>
          )}
      </>
    </CollapsibleSection>
  );
}
